// Conversational Forms — feature state slice (slice 1/5).
//
// Design D1: the shape follows the design reference's state table, adapted to
// this repo's patterns (a reducer persisted per tenant, like appReducer), and is
// the compatibility contract for the four later slices — they add their own keys
// (`queuedForms`, `doneForms`, `helpThreads`, `editingField`) without reshaping
// these.
//
// Adaptation note (resolves design.md's open question): the conversation itself
// stays in `AppState.messages`, the widget's single persisted chat log. The
// prototype's `messages` key is realized as per-turn fields on `ChatMessage`
// (`fills`, `formError`, `retryText`, `formCta`) so a receipt is attached to its
// own turn's message and FIFO ordering is the message order — the alternative, a
// second parallel thread in this slice, would duplicate the log the widget
// already renders, streams into, and persists.

import { FieldValue, FillMode } from './types';

/**
 * The save cue's lifecycle — scoped to real writes (harvest 0.3). `idle` means
 * no write has happened and no cue renders; the demo's timer-driven
 * "Saving…/Saved" over plain local edits claimed persistence that never
 * happened. `error` is the local-retention state: the POST failed, the values
 * stay on the device, the next write re-sends them.
 */
export type SaveCue = 'idle' | 'saving' | 'saved' | 'error';

export const FORMS_STORAGE_PREFIX = 'edullaForms';

export const getFormsStorageIdentifier = (): string =>
    FORMS_STORAGE_PREFIX + (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('edullaTid') : '');

export type CfState = {
    /** The one sticky form (null = nothing started). */
    activeFormId: string | null;
    /**
     * The up-next queue, ordered (design D2): a server offer arriving while a
     * form is active appends; pausing the active form via a switch puts it at
     * the front (the most likely resume). Holds only what the server actually
     * offered or the student actually paused — never a client guess.
     */
    queuedForms: string[];
    /** Committed values — the only thing that counts toward progress. */
    values: Record<string, FieldValue>;
    /** Client-held pending suggestions (platform D4). Never persisted across sessions. */
    suggested: Record<string, FieldValue>;
    /** Blur/submit validation failures, keyed by field id. */
    fieldErrors: Record<string, string>;
    /** Composer text. */
    draft: string;
    /** A turn is in flight. */
    thinking: boolean;
    /** The in-flight turn has passed the slow-turn threshold. */
    slowTurn: boolean;
    /** Forms already started this visitor — suppresses a repeat start card. */
    startedFormIds: string[];
    /** Forms discarded locally — suppression degrades gracefully if dismiss fails. */
    discardedFormIds: string[];
    /** Forms submitted this visitor — unpinned, never re-offered. */
    submittedFormIds: string[];
    /**
     * Forms-corrections slice (D2): submitted forms with an unsent post-submit
     * edit. Session-local by design (design risk note) — excluded from restore
     * in `normalizeFormsState`, exactly like `suggested`/`thinking`: written to
     * storage as part of the state blob but never read back. Only a
     * successful update clears an id; closing the overlay or re-editing back
     * to the original value never does.
     */
    dirtyFormIds: string[];
    /** The discard confirm card is revealed. */
    confirmingDiscard: boolean;
    /** Save cue: tracks the last real POST, never a local edit. */
    saveCue: SaveCue;
    /**
     * Which form the cue belongs to (forms-corrections slice, review-fix):
     * `persist` may target a form other than the active one (an update to a
     * submitted form under review), so the cue's surface — the active form's
     * pinned card — must attribute it correctly rather than assume every
     * write is its own. Null only alongside `saveCue: 'idle'`.
     */
    saveCueFormId: string | null;
    /** The collected-values read has settled (either way). */
    hydrated: boolean;
    /**
     * Fields the visitor has explicitly confirmed in the form view (store
     * tracker 6.64) — a manual edit (`setValue`, dispatched by the form view's
     * `FormOverlay` handlers and, for a sensitive field asked through an
     * inline control, by `InlineFieldAsk` via `commitInlineValue`) or an accepted suggestion (`confirmSuggestion` /
     * `confirmAllSuggestions`, wherever the confirm control lives — the form
     * view, a `FillReceipts` chip, or `PinnedFormCard`'s confirm-all). This
     * is deliberately NOT "has a value" (`statusOf`'s `confirmed`, which also
     * covers a plain chat fill): the platform sends this set as
     * `confirmed_field_ids` on every turn so a field the visitor explicitly
     * locked in the form view is never silently re-filled by extraction,
     * while a chat-committed value that was never form-view-touched stays
     * open to a later chat correction. Pruned of a form's own fields on
     * discard; never touched by `applyFills` — a chat fill is not a
     * confirmation.
     */
    confirmedFieldIds: string[];
    /**
     * Store ledger 6.108 (chips-follow-ask D1): the field the server's latest
     * main-conversation response signalled as its ask. `undefined` + not
     * `askSignalled` = no response has carried the signal (legacy backend);
     * with `askSignalled` true, `null` = nothing asked, no chips.
     */
    askFieldId: string | null | undefined;
    /** The form that was active when the signal arrived (D1, review #12). */
    askFormId: string | null;
    /**
     * Inline sensitive asks: the signalled ask (`askFieldId`) is answered with
     * the field's own input control in the chat (`ask_inline === true`), so no
     * chips. Set and cleared together with `askFieldId` — same rules.
     */
    askInline: boolean;
    /** Has any response carried `ask_field_id`? Never resets (D1 residual). */
    askSignalled: boolean;
};

export const initialFormsState: CfState = {
    activeFormId: null,
    queuedForms: [],
    values: {},
    suggested: {},
    fieldErrors: {},
    draft: '',
    thinking: false,
    slowTurn: false,
    startedFormIds: [],
    discardedFormIds: [],
    submittedFormIds: [],
    dirtyFormIds: [],
    confirmingDiscard: false,
    saveCue: 'idle',
    saveCueFormId: null,
    hydrated: false,
    confirmedFieldIds: [],
    askFieldId: undefined,
    askFormId: null,
    askInline: false,
    askSignalled: false,
};

export type CfAction =
    /** Settle the mount-time read. `values` absent = start from empty (spec: read
     *  endpoint missing). `discardedFormIds` / `submittedFormIds` record lifecycle
     *  states the server reported but the device had not yet — the collected read's
     *  `dismissed` / `submitted` flags, now per registry form (store tracker 6.20). */
    | {
          type: 'hydrate';
          values?: Record<string, FieldValue>;
          activeFormId?: string | null;
          discardedFormIds?: string[];
          submittedFormIds?: string[];
      }
    /** Merge-mode hydration for one form when it becomes active mid-session
     *  (store tracker 6.26a): values the server collected while the form was
     *  inactive fill gaps only. A field whose id already exists in the local
     *  `values` map keeps its local value — even an emptied one, because a
     *  locally cleared field keeps its key with an empty value while a
     *  never-filled field has no key at all, so a clear is never resurrected.
     *  Lifecycle flags behave exactly as in `hydrate`. Unlike `hydrate`, this
     *  never replaces the map, never touches `activeFormId`, and never settles
     *  the mount-time `hydrated` flag. */
    | {
          type: 'hydrateForm';
          values?: Record<string, FieldValue>;
          discardedFormIds?: string[];
          submittedFormIds?: string[];
      }
    | { type: 'setDraft'; draft: string }
    | { type: 'startForm'; formId: string }
    /** A server offer arrived while another form was active — queue it (append).
     *  No-op for a form already active, queued, started, submitted, or discarded
     *  (delta spec: late offers for any of those are no-ops). */
    | { type: 'queueForm'; formId: string }
    /** Quiet dismissal of a queued form: it leaves the queue, nothing else — a
     *  later trigger may re-offer (distinct from discard's server suppression). */
    | { type: 'removeQueued'; formId: string }
    /** Activate `formId`; the paused form (if any) goes to the front of the
     *  queue. Values are untouched — activation only changes which form the
     *  surfaces present (design D1: switching never moves data). */
    | { type: 'switchForm'; formId: string }
    /** Engagement closure (store tracker 6.55): the pinned card's ✕ pauses
     *  the active form — unpin it to the front of the queue, touch no data,
     *  post no acknowledgement. Distinct from `discardForm`, which lives only
     *  in the form view. No-op with nothing active. */
    | { type: 'pauseForm' }
    /** Store ledger 6.108 (D2): the server's ask for the latest
     *  main-conversation response that carried the key (null = no field),
     *  with the form active at that moment. */
    | { type: 'setAskField'; askFieldId: string | null; formId: string | null; askInline?: boolean }
    /** Inline sensitive asks: the visitor skipped `fieldId`'s inline control.
     *  Clears BOTH `askFieldId` and `askInline` (the pair `clearedAsk` clears)
     *  when the ask is this field, so the control collapses and no chips take
     *  its place. A skip for any other field changes nothing. */
    | { type: 'skipInline'; fieldId: string }
    | { type: 'setThinking'; thinking: boolean }
    | { type: 'setSlowTurn'; slowTurn: boolean }
    /** Apply a turn's receipts. `auto` commits; `suggested` parks them pending. */
    | { type: 'applyFills'; fills: Array<{ fieldId: string; value: FieldValue }>; mode: FillMode }
    /** A manual edit — always wins over a pending suggestion for the same field. */
    | { type: 'setValue'; fieldId: string; value: FieldValue }
    | { type: 'confirmSuggestion'; fieldId: string }
    | { type: 'confirmAllSuggestions' }
    | { type: 'undoSuggestion'; fieldId: string }
    | { type: 'setFieldError'; fieldId: string; message: string | null }
    | { type: 'setConfirmingDiscard'; confirming: boolean }
    | { type: 'discardForm'; fieldIds: string[] }
    /** A successful submission — unpin the card, keep the values for corrections. */
    | { type: 'markSubmitted'; formId: string }
    /** Forms-corrections slice (D2): any edit/confirm on a submitted form's
     *  field. Idempotent — a second edit while already dirty is a no-op. */
    | { type: 'setDirty'; formId: string }
    /** Cleared only by a successful update re-post — never by closing the
     *  overlay or by re-editing back to the original value. */
    | { type: 'clearDirty'; formId: string }
    /** A real write's lifecycle — dispatched around the POST, never by an edit.
     *  `formId` is the write's target (review-fix: not always the active
     *  form) so the cue's surface can attribute it correctly; absent/`idle`
     *  clears it. */
    | { type: 'setSaveCue'; cue: SaveCue; formId?: string | null };

const withoutKeys = <T,>(source: Record<string, T>, keys: string[]): Record<string, T> => {
    if (!keys.some((k) => k in source)) return source;
    const next = { ...source };
    keys.forEach((k) => delete next[k]);
    return next;
};

/** Store ledger 6.108 (D4): a lifecycle change clears a received ask until the
 *  next turn re-sets it; a never-signalled (legacy) state is left untouched. */
const clearedAsk = (state: CfState): Partial<CfState> =>
    state.askSignalled ? { askFieldId: null, askInline: false } : {};

const withIds = (list: string[], ids: string[] | undefined): string[] =>
    (ids ?? []).reduce((acc, id) => (acc.includes(id) ? acc : [...acc, id]), list);

/**
 * Auto-mode fill shield (store tracker 6.64): may a chat fill for `fieldId`
 * land? Refused only when the visitor has explicitly form-view-confirmed the
 * field (`confirmedFieldIds`) AND it carries no active validation error — an
 * invalid confirmed field still refills, a correction rather than a clobber
 * (the original 6.20/6.21 rule, now scoped to an explicit confirmation
 * rather than "has any value", so a merely chat-filled field that was never
 * confirmed stays correctable via a later chat message, e.g. through field
 * help). Exported so `FormsContext`'s `commitFills` can suppress the receipt
 * for a fill this same predicate will refuse, without duplicating the rule
 * and risking the two drifting apart.
 */
export const acceptsAutoFill = (state: CfState, fieldId: string): boolean =>
    !state.confirmedFieldIds.includes(fieldId) || Boolean(state.fieldErrors[fieldId]);

/**
 * A restored slice may predate a key or carry a stale in-flight turn. Normalizing
 * on read keeps the reducer total without a version gate, and is where the
 * "pending suggestions are not durable across sessions" rule is enforced.
 */
export const normalizeFormsState = (raw: unknown): CfState => {
    const state = (raw ?? {}) as Partial<CfState>;
    return {
        ...initialFormsState,
        ...state,
        values: state.values ?? {},
        // Queued state survives reloads via the same persistence as values
        // (design D6): a trigger-gated offer is not lost to a refresh.
        queuedForms: state.queuedForms ?? [],
        // Dropped by design (platform D4), not by accident.
        suggested: {},
        fieldErrors: state.fieldErrors ?? {},
        startedFormIds: state.startedFormIds ?? [],
        discardedFormIds: state.discardedFormIds ?? [],
        submittedFormIds: state.submittedFormIds ?? [],
        // Durable, like the other Id lists above (store tracker 6.64): a
        // form-view confirmation is a visitor commitment, not session-local
        // UI state, so it survives a restore exactly as `values` does.
        confirmedFieldIds: state.confirmedFieldIds ?? [],
        // Store ledger 6.108 (D4): the ask signal restores with the slice;
        // an old blob that predates it reads as "never signalled".
        askFieldId: state.askFieldId,
        askFormId: state.askFormId ?? null,
        askInline: state.askInline ?? false,
        askSignalled: state.askSignalled ?? false,
        // Session-local (design D2 risk note): a hard refresh loses which
        // submitted forms had unsent edits, same as any other in-session-only
        // flag below. The values themselves are unaffected — they persist via
        // `values` regardless of `dirtyFormIds`.
        dirtyFormIds: [],
        // A turn cannot survive a reload; never restore a stuck typing indicator.
        thinking: false,
        slowTurn: false,
        confirmingDiscard: false,
        draft: state.draft ?? '',
        // The cue describes a write in *this* session; a restored one is stale.
        saveCue: 'idle',
        saveCueFormId: null,
        hydrated: false,
    };
};

export const formsReducer = (state: CfState, action: CfAction): CfState => {
    switch (action.type) {
        case 'hydrate': {
            const resolvedActiveFormId =
                action.activeFormId !== undefined ? action.activeFormId : state.activeFormId;
            // Store ledger 6.94: a form the submitted record already covers —
            // this device's own `submittedFormIds` or the server's freshly
            // reported ones — can never come back as the active (pinned)
            // form, mirroring the queue invariant below. This only clears the
            // pin; the queue filter just below still keys off
            // `resolvedActiveFormId`, so there is no head promotion here —
            // that only happens on the real Done handoff (`markSubmitted`).
            const activeFormId =
                resolvedActiveFormId !== null &&
                (state.submittedFormIds.includes(resolvedActiveFormId) ||
                    (action.submittedFormIds ?? []).includes(resolvedActiveFormId))
                    ? null
                    : resolvedActiveFormId;
            return {
                ...state,
                // A failed read leaves the local values in place rather than wiping
                // them — start-from-empty is the first-visit case, not a reset.
                // Store tracker 6.64: merged onto the local map, not a wholesale
                // replace — an unsubmitted form's local-only value (one the
                // client holds but this read didn't return, e.g. a form-view
                // edit that reached local state a beat before the server's own
                // record) must survive a same-device reload. The caller
                // (`FormsContext`'s mount effect) already resolves the
                // per-field gap-fill-vs-authoritative choice before building
                // `action.values` — a submitted form's fields arrive
                // unconditionally (authoritative), an unsubmitted form's only
                // where the local map holds no key yet (gap-fill, mirroring
                // `hydrateForm` below) — so a plain merge here is correct for
                // both cases without the reducer needing to know which form
                // owns which field.
                values: action.values ? { ...state.values, ...action.values } : state.values,
                activeFormId,
                // Server-reported lifecycle joins the local record, so the next
                // reload gates on it without another read (tracker 6.20).
                discardedFormIds: withIds(state.discardedFormIds, action.discardedFormIds),
                submittedFormIds: withIds(state.submittedFormIds, action.submittedFormIds),
                // A form the server reports dismissed or submitted has no
                // business waiting in the queue — and, as a total-reducer
                // invariant (adversarial review F1(b), store tracker 6.55),
                // neither does whatever this action resolves `activeFormId`
                // to: a form can never be both pinned and queued in the
                // output state, regardless of which path produced the id
                // (the mount effect's own resume guard, F1(a), is a second,
                // independent line of defense against the specific case that
                // surfaced this — a deliberately paused form the server
                // still reports mid-form values for).
                queuedForms: state.queuedForms.filter(
                    (id) =>
                        id !== resolvedActiveFormId &&
                        !(action.discardedFormIds ?? []).includes(id) &&
                        !(action.submittedFormIds ?? []).includes(id)
                ),
                hydrated: true,
            };
        }

        case 'hydrateForm': {
            // Gap-fill merge: the server value lands only where the local map
            // holds no key. A present key — non-empty (a manual edit on a paused
            // form not yet submitted) or empty (a local clear) — always wins.
            const values = { ...state.values };
            let merged = false;
            Object.entries(action.values ?? {}).forEach(([fieldId, value]) => {
                if (!(fieldId in values)) {
                    values[fieldId] = value;
                    merged = true;
                }
            });
            return {
                ...state,
                values: merged ? values : state.values,
                // Server-reported lifecycle joins the local record, exactly as
                // mount hydration records it (tracker 6.20).
                discardedFormIds: withIds(state.discardedFormIds, action.discardedFormIds),
                submittedFormIds: withIds(state.submittedFormIds, action.submittedFormIds),
                queuedForms: state.queuedForms.filter(
                    (id) =>
                        !(action.discardedFormIds ?? []).includes(id) &&
                        !(action.submittedFormIds ?? []).includes(id)
                ),
            };
        }

        case 'setDraft':
            return { ...state, draft: action.draft };

        case 'startForm':
            if (state.activeFormId === action.formId) return state;
            return {
                ...state,
                activeFormId: action.formId,
                ...clearedAsk(state),
                // Starting a form it was queued behind consumes its queue entry.
                queuedForms: state.queuedForms.filter((id) => id !== action.formId),
                confirmingDiscard: false,
                startedFormIds: state.startedFormIds.includes(action.formId)
                    ? state.startedFormIds
                    : [...state.startedFormIds, action.formId],
            };

        case 'queueForm': {
            // Queue only a genuinely offerable form: the delta spec makes a late
            // offer for an active, queued, started, submitted, or discarded form
            // a no-op. (A paused form is started — its way back is the queue row
            // it already has, or the menu.)
            const suppressed =
                state.activeFormId === action.formId ||
                state.queuedForms.includes(action.formId) ||
                state.startedFormIds.includes(action.formId) ||
                state.submittedFormIds.includes(action.formId) ||
                state.discardedFormIds.includes(action.formId);
            if (suppressed) return state;
            return { ...state, queuedForms: [...state.queuedForms, action.formId] };
        }

        case 'removeQueued':
            if (!state.queuedForms.includes(action.formId)) return state;
            return { ...state, queuedForms: state.queuedForms.filter((id) => id !== action.formId) };

        case 'switchForm': {
            if (state.activeFormId === action.formId) return state;
            const paused = state.activeFormId;
            const rest = state.queuedForms.filter((id) => id !== action.formId);
            return {
                ...state,
                activeFormId: action.formId,
                ...clearedAsk(state),
                // Pause → front of queue (design D2: the most likely resume).
                queuedForms: paused ? [paused, ...rest] : rest,
                confirmingDiscard: false,
                startedFormIds: state.startedFormIds.includes(action.formId)
                    ? state.startedFormIds
                    : [...state.startedFormIds, action.formId],
            };
        }

        case 'pauseForm': {
            const paused = state.activeFormId;
            if (!paused) return state;
            const rest = state.queuedForms.filter((id) => id !== paused);
            return {
                ...state,
                activeFormId: null,
                ...clearedAsk(state),
                // Pause → front of queue, exactly like `switchForm`'s own
                // pause-the-outgoing-form placement (design D2: the most
                // likely resume).
                queuedForms: [paused, ...rest],
                confirmingDiscard: false,
            };
        }

        case 'setAskField':
            return {
                ...state,
                askFieldId: action.askFieldId,
                askFormId: action.formId,
                askInline: action.askInline === true,
                askSignalled: true,
            };

        case 'skipInline':
            return state.askFieldId === action.fieldId
                ? { ...state, askFieldId: null, askInline: false }
                : state;

        case 'setThinking':
            return { ...state, thinking: action.thinking, slowTurn: action.thinking ? state.slowTurn : false };

        case 'setSlowTurn':
            return { ...state, slowTurn: action.slowTurn };

        case 'applyFills': {
            if (!action.fills.length) return state;
            if (action.mode === 'auto') {
                // A field the visitor has explicitly form-view-confirmed is
                // closed (store tracker 6.64, `acceptsAutoFill`): the
                // open-field list already tells extraction so, but a
                // misbehaving server must never clobber a confirmed value
                // from here either — the reducer is the last line of
                // defense. A merely chat-filled field that was never
                // confirmed stays open to a later chat correction (the
                // over-block guard), and an invalid confirmed field still
                // refills (that is a correction, not a clobber). Suggested
                // mode is untouched: suggestions never commit without the
                // student's confirm.
                const fills = action.fills.filter((f) => acceptsAutoFill(state, f.fieldId));
                if (!fills.length) return state;
                const values = { ...state.values };
                fills.forEach((f) => { values[f.fieldId] = f.value; });
                return {
                    ...state,
                    values,
                    // A committed value supersedes any suggestion for that field.
                    suggested: withoutKeys(state.suggested, fills.map((f) => f.fieldId)),
                    fieldErrors: withoutKeys(state.fieldErrors, fills.map((f) => f.fieldId)),
                };
            }
            const suggested = { ...state.suggested };
            action.fills.forEach((f) => { suggested[f.fieldId] = f.value; });
            return { ...state, suggested };
        }

        case 'setValue':
            return {
                ...state,
                values: { ...state.values, [action.fieldId]: action.value },
                // Edit beats suggestion (platform D4).
                suggested: withoutKeys(state.suggested, [action.fieldId]),
                fieldErrors: withoutKeys(state.fieldErrors, [action.fieldId]),
                // Store tracker 6.64: `setValue` is dispatched only for the
                // visitor's own explicit sets — `FormOverlay`'s
                // `handleChange`/`handleAddressPick` (form-view actions) and
                // `FormsContext.commitInlineValue` (a sensitive field set in
                // the chat's inline control, which holds the same local
                // form-view-value status) — so every `setValue` is an explicit
                // confirmation. A chat fill never reaches here; it always goes
                // through `applyFills` instead, which deliberately never
                // touches this list.
                confirmedFieldIds: withIds(state.confirmedFieldIds, [action.fieldId]),
            };

        case 'confirmSuggestion': {
            if (!(action.fieldId in state.suggested)) return state;
            return {
                ...state,
                values: { ...state.values, [action.fieldId]: state.suggested[action.fieldId] },
                suggested: withoutKeys(state.suggested, [action.fieldId]),
                // An explicit accept, wherever its control lives — the form
                // view, a `FillReceipts` chip, or `PinnedFormCard`'s
                // confirm-all (store tracker 6.64).
                confirmedFieldIds: withIds(state.confirmedFieldIds, [action.fieldId]),
            };
        }

        case 'confirmAllSuggestions': {
            const ids = Object.keys(state.suggested);
            if (!ids.length) return state;
            return {
                ...state,
                values: { ...state.values, ...state.suggested },
                suggested: {},
                confirmedFieldIds: withIds(state.confirmedFieldIds, ids),
            };
        }

        case 'undoSuggestion':
            if (!(action.fieldId in state.suggested)) return state;
            return { ...state, suggested: withoutKeys(state.suggested, [action.fieldId]) };

        case 'setFieldError':
            if (action.message === null) {
                return { ...state, fieldErrors: withoutKeys(state.fieldErrors, [action.fieldId]) };
            }
            return { ...state, fieldErrors: { ...state.fieldErrors, [action.fieldId]: action.message } };

        case 'setConfirmingDiscard':
            return { ...state, confirmingDiscard: action.confirming };

        case 'discardForm': {
            const discarded = state.activeFormId;
            // Multi-form handoff: a never-started queue head is popped because
            // the provider offers it as the replacement in the discard
            // acknowledgement (delta spec: discard-with-queue). A STARTED head —
            // a paused form — stays queued: the start card suppresses started
            // forms, so popping it would strand the form with neither a card nor
            // its queue row (store tracker 6.28).
            const head = state.queuedForms[0];
            const popHead = head !== undefined && !state.startedFormIds.includes(head);
            return {
                ...state,
                values: withoutKeys(state.values, action.fieldIds),
                suggested: withoutKeys(state.suggested, action.fieldIds),
                fieldErrors: withoutKeys(state.fieldErrors, action.fieldIds),
                // Store tracker 6.64: a discarded form's fields leave the
                // confirmed set along with everything else — nothing is left
                // behind to shield a field that no longer has a value at all.
                confirmedFieldIds: state.confirmedFieldIds.filter((id) => !action.fieldIds.includes(id)),
                activeFormId: null,
                ...clearedAsk(state),
                queuedForms: popHead ? state.queuedForms.slice(1) : state.queuedForms,
                confirmingDiscard: false,
                discardedFormIds:
                    discarded && !state.discardedFormIds.includes(discarded)
                        ? [...state.discardedFormIds, discarded]
                        : state.discardedFormIds,
            };
        }

        case 'markSubmitted': {
            // Post-submit handoff mirrors discard: a never-started queue head
            // leaves the queue and rides the acknowledgement as a fresh offer.
            // A STARTED head — a paused form — stays queued, keeping its Up next
            // row, because the start card suppresses started forms and popping
            // it would strand the form (store tracker 6.28). Only an active
            // form's submission hands off; either way the submitted form itself
            // never lingers in the queue.
            const remaining = state.queuedForms.filter((id) => id !== action.formId);
            const head = remaining[0];
            const popHead =
                state.activeFormId === action.formId &&
                head !== undefined &&
                !state.startedFormIds.includes(head);
            return {
                ...state,
                activeFormId: state.activeFormId === action.formId ? null : state.activeFormId,
                queuedForms: popHead ? remaining.slice(1) : remaining,
                suggested: {},
                fieldErrors: {},
                ...clearedAsk(state),
                confirmingDiscard: false,
                submittedFormIds: state.submittedFormIds.includes(action.formId)
                    ? state.submittedFormIds
                    : [...state.submittedFormIds, action.formId],
            };
        }

        case 'setDirty':
            if (state.dirtyFormIds.includes(action.formId)) return state;
            return { ...state, dirtyFormIds: [...state.dirtyFormIds, action.formId] };

        case 'clearDirty':
            if (!state.dirtyFormIds.includes(action.formId)) return state;
            return {
                ...state,
                dirtyFormIds: state.dirtyFormIds.filter((id) => id !== action.formId),
            };

        case 'setSaveCue':
            return {
                ...state,
                saveCue: action.cue,
                saveCueFormId: action.cue === 'idle' ? null : action.formId ?? null,
            };

        default:
            return state;
    }
};
